/**
 * The campaign page's small pictures: one set, drawn here, no library.
 *
 * Same grid, stroke and reasoning as `NavIcon` — a 24-unit box, a 1.7 stroke,
 * `currentColor`, `aria-hidden` unless a label is given — so a reference link's
 * icon and the sidebar's icon beside it are visibly the same family. The
 * platform marks are drawn as line glyphs rather than the platforms' own logos,
 * because a brand logo carries its own colour and its own weight and would be
 * the one thing on the page not drawn from the tokens.
 */

export type GlyphName =
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'doc'
  | 'vod'
  | 'channel'
  | 'file'
  | 'link'
  | 'people'
  | 'lock';

const PATHS: Record<GlyphName, string[]> = {
  /* A rounded screen with a play mark in it. */
  youtube: [
    'M3.5 8.2A2.7 2.7 0 0 1 6.2 5.5h11.6a2.7 2.7 0 0 1 2.7 2.7v7.6a2.7 2.7 0 0 1-2.7 2.7H6.2a2.7 2.7 0 0 1-2.7-2.7Z',
    'm10.3 9.2 4.6 2.8-4.6 2.8Z',
  ],
  /* The rounded square, the lens, the dot. */
  instagram: [
    'M4 8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4Z',
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z',
    'M16.6 7.4h.01',
  ],
  /* A note: the stem, the head, the flourish at the top. */
  tiktok: ['M13.5 4.5v10.2a3.3 3.3 0 1 1-3.3-3.3', 'M13.5 4.5a4.3 4.3 0 0 0 4.3 4.3'],
  /* A page with three lines. */
  doc: ['M6.5 3.5h7l4 4v13h-11Z', 'M13.5 3.5v4h4', 'M9 11.5h6', 'M9 15h6'],
  /* A play mark in a circle: a recording to cut from. */
  vod: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'm10.2 9 4.6 3-4.6 3Z'],
  /* A screen on a stand: a channel, somebody's stream. */
  channel: ['M3.5 5.5h17v11h-17Z', 'M8.5 20.5h7', 'M12 16.5v4'],
  /* A page with a folded corner and a down arrow: something to download. */
  file: ['M6.5 3.5h7l4 4v13h-11Z', 'M13.5 3.5v4h4', 'M12 10v7', 'm9.5 14.5 2.5 2.5 2.5-2.5'],
  /* Two links of a chain. */
  link: [
    'M10.5 13.5a4 4 0 0 0 5.9.3l2.3-2.3a4 4 0 0 0-5.66-5.66l-1.3 1.3',
    'M13.5 10.5a4 4 0 0 0-5.9-.3l-2.3 2.3a4 4 0 1 0 5.66 5.66l1.3-1.3',
  ],
  /* Two people: the participants on a campaign. */
  people: [
    'M9.5 12a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z',
    'M2.5 20a7 7 0 0 1 14 0',
    'M16.5 5.4a3.75 3.75 0 0 1 0 7.2',
    'M18 15.2A6 6 0 0 1 21.5 20',
  ],
  /* A padlock: apply first. */
  lock: ['M5.5 11h13v9.5h-13Z', 'M8.5 11V7.5a3.5 3.5 0 0 1 7 0V11', 'M12 15v2'],
};

export function Glyph({
  name,
  size = 18,
  label,
  className,
}: {
  name: GlyphName;
  size?: number;
  /** A name makes the picture a labelled image; without one it is decoration. */
  label?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <svg
      className={className ? `glyph ${className}` : 'glyph'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      focusable="false"
    >
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * Gold, silver, bronze.
 *
 * A disc with the rank in it and a ribbon under it, coloured by a token per
 * metal so the light theme gets its own three. NOT AN EMOJI: a medal emoji is
 * drawn by whichever font the viewer's platform ships, is a different picture
 * on every operating system, and cannot be recoloured for the ground it sits
 * on. The numeral stays inside the picture, so a screen reader is given
 * "Gold medal, first place" once and the rank column beside it says "#1".
 */
const MEDALS = ['gold', 'silver', 'bronze'] as const;
const PLACES = ['first', 'second', 'third'] as const;

export function Medal({ rank, size = 22 }: { rank: number; size?: number }): React.JSX.Element | null {
  const metal = MEDALS[rank - 1];
  if (!metal) return null;
  return (
    <svg
      className="medal"
      data-metal={metal}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={`${metal[0]!.toUpperCase()}${metal.slice(1)} medal, ${PLACES[rank - 1]} place`}
      focusable="false"
    >
      <path className="medal-ribbon" d="M8 2.5h8l-1.5 6h-5Z" />
      <circle className="medal-disc" cx="12" cy="15" r="6.5" />
      <text
        className="medal-rank"
        x="12"
        y="15"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="8"
        fontWeight="700"
      >
        {rank}
      </text>
    </svg>
  );
}
